"use client"

import React, { useState, Fragment } from "react";
import {
  Dialog,
  Transition,
  TransitionChild,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { AlertCircle, X } from "lucide-react";
import { Button } from "nextra/components";

const apiUrl = process.env.NEXT_PUBLIC_API_URL ?? "https://api.limbar.io";

interface AlertProps {
  status: "success" | "error";
  message: string;
}

interface FeedbackComponentProps {
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

const Alert: React.FC<AlertProps> = ({ status, message }) => (
  <div
    className={`mt-4 p-4 rounded-md ${status === "success" ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`}
  >
    <div className="flex">
      <div className="shrink-0">
        <AlertCircle className="h-5 w-5" />
      </div>
      <div className="ml-3">
        <h3 className="text-sm font-medium">
          {status === "success" ? "Success" : "Error"}
        </h3>
        <div className="mt-2 text-sm">{message}</div>
      </div>
    </div>
  </div>
);

const FeedbackComponent: React.FC<FeedbackComponentProps> = ({ onClick }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitStatus, setSubmitStatus] = useState<"success" | "error" | null>(
    null,
  );

  const openModal = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) onClick(e);
    setIsOpen(true);
  };

  const closeModal = () => setIsOpen(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Replace with your actual API endpoint
      const response = await fetch(`${apiUrl}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: feedback, email: email }),
      });

      if (response.ok) {
        setSubmitStatus("success");
        setFeedback("");
        setEmail("");
        setTimeout(() => {
          closeModal();
          setSubmitStatus(null);
        }, 2000);
      } else {
        throw new Error("Failed to submit feedback");
      }
    } catch (error) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <span onClick={openModal}>Question? Give us feedback →</span>

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                  <DialogTitle
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                  >
                    Share your thoughts
                  </DialogTitle>
                  <form onSubmit={handleSubmit}>
                    <div className="mt-2">
                      <input
                        type="email"
                        value={email}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setEmail(e.target.value)
                        }
                        className="w-full px-3 py-2 mb-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden"
                        placeholder="Your email address"
                        required
                      />
                      <textarea
                        value={feedback}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                          setFeedback(e.target.value)
                        }
                        className="w-full px-3 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden"
                        rows={4}
                        placeholder="Your feedback here..."
                        required
                      />
                    </div>

                    {submitStatus === "success" && (
                      <Alert
                        status="success"
                        message="Your feedback has been submitted successfully."
                      />
                    )}

                    {submitStatus === "error" && (
                      <Alert
                        status="error"
                        message="Failed to submit feedback. Please try again."
                      />
                    )}

                    <div className="mt-4 flex justify-end">
                      <Button
                        type="button"
                        className="mr-2 px-4 py-2"
                        onClick={closeModal}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className="px-4 py-2"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Submitting..." : "Submit"}
                      </Button>
                    </div>
                  </form>
                  <button
                    onClick={closeModal}
                    className="absolute top-2 right-2 text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-200"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default FeedbackComponent;
